import { cn } from "@core-ui/utils/cn";
import { useFocusKeepingPress } from "@ui-kit/hooks/useFocusKeepingPress";
import { DisabledReason, Tooltip, TooltipContent, TooltipShortcut, TooltipTrigger } from "@ui-kit/Tooltip";
import { GlassToolbarButton as UiKitGlassToolbarButton } from "ics-ui-kit/components/glass-toolbar";
import { forwardRef, useCallback } from "react";
import type { ExtractComponentGeneric } from "../../lib/extractComponentGeneric";

type UiKitGlassToolbarButtonProps = ExtractComponentGeneric<typeof UiKitGlassToolbarButton>;

export interface GlassToolbarToggleButtonProps extends UiKitGlassToolbarButtonProps {
	active?: boolean;
	tooltipText?: string;
	/**
	 * Why the button is off right now. A button turned off by the native attribute drops out of the
	 * tab order and stops receiving pointer events, so without this its tooltip reaches nobody.
	 */
	disabledReason?: string;
	hotKey?: string;
	focusable?: boolean;
}

export const GlassToolbarToggleButton = forwardRef<HTMLButtonElement, GlassToolbarToggleButtonProps>((props, ref) => {
	const { tooltipText, disabledReason, hotKey, active, focusable, onClick, onMouseDown, className, ...otherProps } =
		props;
	const state = active ? "on" : "off";

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			if (!focusable) e.preventDefault();
			onClick?.(e);
		},
		[onClick, focusable],
	);

	const handleMouseDown = useFocusKeepingPress(focusable, onMouseDown);

	const button = (
		<UiKitGlassToolbarButton
			aria-label={tooltipText}
			ref={ref}
			{...otherProps}
			className={cn(
				"disabled:pointer-events-none disabled:opacity-50 hover:bg-alpha-high-90 data-[state=on]:bg-alpha-high-80 aria-expanded:bg-alpha-high-80",
				className,
			)}
			data-state={state}
			onClick={handleClick}
			onMouseDown={handleMouseDown}
		/>
	);

	// A button turned off by the native attribute takes no pointer events and leaves the tab order,
	// so its tooltip opens for nobody. The reason is handed over instead, on hover and on focus alike.
	if (otherProps.disabled && (disabledReason || tooltipText))
		return (
			<DisabledReason
				reason={
					<div className="flex flex-col gap-0.5">
						{tooltipText}
						{disabledReason && <span className="text-muted">{disabledReason}</span>}
					</div>
				}
			>
				{button}
			</DisabledReason>
		);

	if (!tooltipText && !hotKey) return button;

	return (
		<Tooltip>
			{(hotKey || tooltipText) && (
				<TooltipContent sideOffset={2}>
					<div className="flex items-center gap-2">
						{tooltipText}
						{hotKey && <TooltipShortcut className="p-0" inverse value={hotKey} />}
					</div>
				</TooltipContent>
			)}
			<TooltipTrigger asChild>{button}</TooltipTrigger>
		</Tooltip>
	);
});
